/**
 * 서버 연결 + 월드 상태의 로컬 복제본.
 *
 * 핵심 규약: 월드 상태는 서버가 소유한다. 이 파일은 델타를 순서대로 적용하기만 하고,
 * rev 가 한 칸이라도 어긋나면 즉시 풀 스냅샷을 다시 받아 완전히 맞춘다.
 */

import {
  PROTOCOL_VERSION,
  type ClientMsg,
  type ObjState,
  type Phase,
  type PlayerPose,
  type PlayerState,
  type ServerMsg,
  type WorldState,
} from '../../shared/protocol';
import { epMaybe } from './episodeData';

export interface PoseSample {
  /** 수신 시각(로컬 performance.now 기준) */
  at: number;
  pose: PlayerPose;
}

type Events = {
  ready: (you: string) => void;
  players: () => void;
  world: () => void;
  chat: (m: { id: string; name: string; color: string; m: string }) => void;
  log: (m: string) => void;
  deny: (why: string) => void;
  note: (m: string) => void;
  /** 선택형 조사 — exam 끝난 기계의 선택지를 열어라 (prop id) */
  choose: (id: string) => void;
  /** [R] 재생 — 누군가 기계를 틀었다. 방 전원이 듣는다 (prop id → def.play 로 파일 해석) */
  play: (id: string) => void;
  bodypart: (r: { part: string; text: string; clueId?: string }) => void;
  mark: (m: { id: string; name: string; color: string; x: number; y: number; z: number }) => void;
  status: (s: NetStatus) => void;
  /** 내 대본 — 서버가 나에게만 보낸 것 */
  script: (s: {
    castId: string;
    culprit: boolean;
    sections: { title: string; body: string }[];
  }) => void;
  verdict: (v: {
    accused: string;
    culpritCast: string;
    caught: boolean;
    tally: { id: string; n: number }[];
    truth: string;
  }) => void;
};

export type NetStatus = 'connecting' | 'open' | 'closed' | 'error';

/**
 * **방 서버 목록** — 앞에서부터 붙어 본다.
 *
 * 2026-10-01 부터 Cloudflare 하나(제 서버) — 도쿄 Oracle VM 방 서버(cs-tokyo)는 내렸다(그 자리는 Murder Mystery Online 이 쓴다).
 * 도쿄 Node 판(server/node/host.mjs)은 코드만 남아 있다.
 *
 * 시험용: `?net=wss://…` 그 주소 먼저
 */
export function netServers(): string[] {
  const here = `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}`;
  const q = new URLSearchParams(location.search).get('net');
  if (q && /^wss?:\/\//.test(q)) return [q.replace(/\/+$/, ''), here];
  return [here];
}

/** 방 요약(/api/room) — 방 서버 목록 순서대로 묻는다. 도쿄는 제게 없고 Cloudflare 에서 도는 방이면 그쪽 답을 준다 */
export async function fetchRoomInfo(room: string): Promise<{ exists?: boolean; episode?: string } | null> {
  for (const s of netServers()) {
    try {
      const r = await fetch(`${s.replace(/^ws/, 'http')}/api/room/${encodeURIComponent(room)}`, {
        cache: 'no-store',
        signal: AbortSignal.timeout(3000),
      });
      if (r.ok) return await r.json();
    } catch {
      /* 다음 서버 */
    }
  }
  return null;
}

export class Net {
  ws: WebSocket | null = null;
  you = '';
  room: string;
  status: NetStatus = 'connecting';
  rtt = 0;

  world: WorldState = {
    phase: 'lobby',
    round: 1,
    objects: epMaybe()?.initialObjects() ?? {},
    clues: {},
    taken: {},
    shots: {},
    reports: {},
    budget: {},
    assigned: {},
    ready: {},
    unlocked: {},
    out: [],
    done: [],
    pairing: {},
    turnEndsAt: 0,
    roles: {},
    speaker: '',
    briefDone: [],
    stageEndsAt: 0,
    board: [],
    flags: {},
    votes: {},

  };
  rev = 0;
  /** 서버가 배정한 스폰 위치 (x, z, yaw) */
  spawn: [number, number, number] | null = null;
  players = new Map<string, PlayerState>();
  /** 원격 플레이어 보간 버퍼 (id -> 최근 샘플) */
  buffers = new Map<string, PoseSample[]>();

  private handlers: { [K in keyof Events]?: Events[K][] } = {};
  private pingSeq = 0;
  private pingSentAt = 0;
  private retry = 0;
  private closedByUser = false;
  private lastMoveSent = 0;
  private lastReport: [number, number, number, number, number] | null = null;
  private lastPos: [number, number] = [0, 0];
  /** 마지막으로 보고한(하려던) 발 높이 — 다시 붙을 때 서 있던 자리를 알린다 */
  private lastY = 0;
  private keepaliveOn = false;
  private queue: string[] = [];
  /** 방 서버 목록(netServers)과 지금 쓰는 것 */
  private servers = netServers();
  private srv = 0;
  /**
   * 지금 서버에 붙은 적이 있나. **붙은 적 있는 서버에는 계속 다시 붙는다** — 판이 거기 있다(서버를 다시 켜는 배포는 몇 초면 돌아온다).
   * 붙은 적 없는 서버는 실패하자마자 다음 서버로 간다
   */
  private joined = false;
  /** 붙어 있던 서버가 끊긴 뒤 다시 못 붙고 있는 시작 시각. 30초 넘으면 그 서버가 죽었다고 보고 다음 서버로 */
  private downSince = 0;

  constructor(
    private roomCode: string,
    private name: string,
    private color: string,
    /**
     * 새 방을 열 때 고른 사건. **초대로 들어갈 때는 안 보낸다** —
     * 방이 이미 정해 뒀고, 서버는 첫 판이 아니면 이 값을 무시한다
     */
    private episode?: string,
  ) {
    this.room = roomCode;
  }

  on<K extends keyof Events>(k: K, fn: Events[K]) {
    (this.handlers[k] ??= [] as never).push(fn as never);
  }

  private emit<K extends keyof Events>(k: K, ...args: Parameters<Events[K]>) {
    for (const fn of this.handlers[k] ?? []) (fn as (...a: unknown[]) => void)(...args);
  }

  connect() {
    // 중복 연결 방지 — 백오프 타이머와 수동 호출이 겹칠 수 있다
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    if (this.ws) {
      this.ws.onopen = this.ws.onmessage = this.ws.onerror = this.ws.onclose = null;
    }
    this.closedByUser = false;
    this.setStatus('connecting');
    const ep = this.episode ? `&ep=${encodeURIComponent(this.episode)}` : '';
    const ws = new WebSocket(`${this.servers[this.srv]}/ws?room=${encodeURIComponent(this.roomCode)}${ep}`);
    this.ws = ws;
    let opened = false;
    // 서버가 꺼져 있으면 연결이 한참 매달린다 — 5초 안에 안 열리면 실패로 친다
    const openTimer = setTimeout(() => { if (ws.readyState === WebSocket.CONNECTING) ws.close(); }, 5000);

    ws.onopen = () => {
      clearTimeout(openTimer);
      opened = true;
      this.joined = true;
      this.downSince = 0;
      this.retry = 0;
      this.setStatus('open');
      /* 이 탭이 이 방에서 받은 id·표가 있으면 **같은 사람으로 이어 받는다**(room.ts onHello) — 배역·조사 횟수·찍은 것이 남는다.
         서버 재시작·와이파이 끊김·새로고침 모두. 어느 서버든 같은 판이면 받아 준다 */
      const sess = this.session();
      const at = this.lastReport;
      this.send({
        t: 'hello', v: PROTOCOL_VERSION, name: this.name, color: this.color,
        ...(sess ? { id: sess.id, tok: sess.tok } : {}),
        ...(sess && at ? { pose: [r3(at[0]), r3(this.lastY), r3(at[1]), r3(at[2])] as [number, number, number, number] } : {}),
      });
      for (const q of this.queue.splice(0)) ws.send(q);
      this.startPing();
      this.startKeepalive();
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return;
      let msg: ServerMsg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      this.handle(msg);
    };
    ws.onerror = () => this.setStatus('error');
    ws.onclose = (ev) => {
      clearTimeout(openTimer);
      this.setStatus('closed');
      if (this.closedByUser) return;
      const n = this.servers.length;
      if (ev.code === 4010 && n > 1) {
        // 이 방은 Cloudflare 에서 돌고 있다(host.mjs) — 거기로. 목록의 마지막이 Cloudflare 다
        this.useServer(n - 1);
        this.connect();
        return;
      }
      if (!opened && n > 1) {
        if (!this.joined) this.useServer((this.srv + 1) % n);
        else if (!this.downSince) this.downSince = Date.now();
        else if (Date.now() - this.downSince > 30_000) this.useServer((this.srv + 1) % n);
      }
      // 지수 백오프 재접속. 재접속 시 서버가 풀 스냅샷을 다시 준다.
      const delay = Math.min(8000, 400 * 2 ** this.retry++);
      setTimeout(() => this.connect(), delay);
    };
  }

  private session(): { id: string; tok: string } | null {
    try {
      const j = JSON.parse(sessionStorage.getItem(`cs.sess.${this.roomCode}`) ?? 'null');
      return j && typeof j.id === 'string' && typeof j.tok === 'string' ? j : null;
    } catch {
      return null;
    }
  }

  /** 지금 붙는(붙은) 방 서버 주소 — 검증용 */
  server(): string {
    return this.servers[this.srv];
  }

  private useServer(i: number) {
    if (i !== this.srv) console.info('[net] 방 서버 →', this.servers[i]);
    this.srv = i;
    this.joined = false;
    this.downSince = 0;
    this.retry = 0;
  }

  disconnect() {
    this.closedByUser = true;
    this.ws?.close();
  }

  private setStatus(s: NetStatus) {
    this.status = s;
    this.emit('status', s);
  }

  private handle(msg: ServerMsg) {
    switch (msg.t) {
      case 'welcome': {
        this.you = msg.you;
        if (msg.tok) {
          try { sessionStorage.setItem(`cs.sess.${this.roomCode}`, JSON.stringify({ id: msg.you, tok: msg.tok })); } catch { /* 저장 불가 — 이어 받기만 못 한다 */ }
        }
        this.rev = msg.rev;
        this.world = msg.world;
        this.players = new Map(msg.players.map((p) => [p.id, p]));
        this.buffers.clear();
        for (const pose of msg.poses) this.pushPose(pose);
        const mine = msg.poses.find((p) => p[0] === msg.you);
        this.spawn = mine ? [mine[1], mine[3], mine[4]] : null;
        this.emit('ready', msg.you);
        this.emit('players');
        this.emit('world');
        break;
      }
      case 'snap': {
        // 도쿄 서버는 자리를 50ms 씩 모아 보낸다 — 들고 있던 만큼 앞당겨 찍어야 샘플 간격이 원래대로다(room.ts flushPoses)
        const now = performance.now();
        msg.poses.forEach((pose, i) => this.pushPose(pose, now - (msg.held?.[i] ?? 0)));
        break;
      }
      case 'world': {
        // rev 갭 검사 — 하나라도 놓쳤으면 통째로 다시 받는다
        if (msg.rev - msg.ops.length !== this.rev) {
          this.send({ t: 'resync' });
          break;
        }
        for (const op of msg.ops) {
          if (op.op === 'set') {
            const cur = (this.world.objects[op.id] ??= {});
            for (const [k, v] of Object.entries(op.f)) {
              /* 서버는 삭제를 null 로 싣는다 — undefined 는 JSON 을 못 넘어와
               * 옛 값이 영영 남았다 (내려놓기 p, 든 사람 by) (2026-09-18) */
              if (v === undefined || v === null) delete (cur as Record<string, unknown>)[k];
              else (cur as Record<string, unknown>)[k] = v;
            }
          } else if (op.op === 'clue') {
            this.world.clues[op.c.id] = op.c;
          } else if (op.op === 'taken') {
            (this.world.taken ??= {})[op.id] = true;
          } else if (op.op === 'shot') {
            (this.world.shots ??= {})[op.id] = true;
          } else if (op.op === 'report') {
            (this.world.reports ??= {})[op.id] = true;
          } else if (op.op === 'round') {
            this.world.round = op.v;
            this.world.budget = op.budget;
          } else if (op.op === 'budget') {
            this.world.budget[op.id] = op.v;
          } else if (op.op === 'assign') {
            this.world.assigned = op.a;
          } else if (op.op === 'unlock') {
            this.world.unlocked[op.id] = true;
          } else if (op.op === 'pairing') {
            this.world.pairing = op.v;
          } else if (op.op === 'roles') {
            this.world.roles = op.v;
          } else if (op.op === 'ready') {
            if (op.v) this.world.ready[op.id] = true;
            else delete this.world.ready[op.id];
          } else if (op.op === 'stage') {
            this.world.phase = op.phase;
            this.world.speaker = op.speaker;
            this.world.briefDone = op.done;
            this.world.stageEndsAt = op.endsAt;
          } else if (op.op === 'board') {
            this.world.board = op.v;
          } else if (op.op === 'flags') {
            this.world.flags[op.id] = op.v;
          } else if (op.op === 'votes') {
            this.world.votes = op.v;

          } else if (op.op === 'turn') {
            this.world.out = op.out;
            this.world.done = op.done;
            this.world.turnEndsAt = op.endsAt;
          } else if (op.op === 'phase') {
            this.world.phase = op.v;
          }
        }
        this.rev = msg.rev;
        this.emit('world');
        break;
      }
      case 'full': {
        this.rev = msg.rev;
        this.world = msg.world;
        this.players = new Map(msg.players.map((p) => [p.id, p]));
        this.emit('players');
        this.emit('world');
        break;
      }
      case 'join':
        this.players.set(msg.p.id, msg.p);
        this.emit('players');
        break;
      case 'leave':
        this.players.delete(msg.id);
        this.buffers.delete(msg.id);
        this.emit('players');
        break;
      case 'verdict':
        this.emit('verdict', msg);
        break;
      case 'script':
        this.emit('script', msg);
        break;
      case 'chat':
        this.emit('chat', msg);
        break;
      case 'log':
        this.emit('log', msg.m);
        break;
      case 'mark':
        this.emit('mark', msg);
        break;
      case 'deny':
        this.emit('deny', msg.why);
        break;
      case 'note':
        this.emit('note', msg.m);
        break;
      case 'choose':
        this.emit('choose', msg.id);
        break;
      case 'play':
        this.emit('play', msg.id);
        break;
      case 'bodypart':
        this.emit('bodypart', msg);
        break;
      case 'pong':
        if (msg.c === this.pingSeq) this.rtt = Math.round(performance.now() - this.pingSentAt);
        break;
    }
  }

  private pushPose(pose: PlayerPose, at = performance.now()) {
    const id = pose[0];
    if (id === this.you) return;
    const buf = this.buffers.get(id) ?? [];
    buf.push({ at, pose });
    // 1초치만 유지
    while (buf.length > 24) buf.shift();
    this.buffers.set(id, buf);
  }

  /**
   * rAF 와 무관한 keepalive.
   * 탭이 백그라운드로 가면 rAF 가 멈춰 게임 루프의 reportMove 도 멈춘다.
   * 그러면 서버/다른 참가자 입장에서 이 플레이어는 조용히 사라진 것처럼 보인다.
   */
  private startKeepalive() {
    if (this.keepaliveOn) return;
    this.keepaliveOn = true;
    const tick = () => {
      // onclose 가 오지 않는 경우가 실제로 있다(소켓이 CLOSING 에서 멈춤).
      // 그러면 이벤트만 믿는 재접속은 영영 안 걸리므로 여기서 직접 확인해 되살린다.
      if (this.ws?.readyState !== WebSocket.OPEN) {
        if (!this.closedByUser) {
          if (this.status === 'open') this.setStatus('closed');
          this.connect();
        }
        setTimeout(tick, 1200);
        return;
      }
      const last = this.lastReport;
      if (last && performance.now() - this.lastMoveSent > 1800) {
        this.lastMoveSent = performance.now();
        this.send({
          t: 'move',
          x: r3(this.lastPos[0]),
          y: 0,
          z: r3(this.lastPos[1]),
          yaw: r3(last[2]),
          pitch: r3(last[3]),
          flags: last[4],
        });
      }
      setTimeout(tick, 2000);
    };
    setTimeout(tick, 2000);
  }

  private startPing() {
    const tick = () => {
      if (this.ws?.readyState !== WebSocket.OPEN) return;
      this.pingSeq = (this.pingSeq + 1) & 0xffff;
      this.pingSentAt = performance.now();
      this.send({ t: 'ping', c: this.pingSeq });
      setTimeout(tick, 2000);
    };
    setTimeout(tick, 300);
  }

  send(msg: ClientMsg) {
    const s = JSON.stringify(msg);
    if (this.ws?.readyState === WebSocket.OPEN) {
      /* reportMove 를 안 타고 바로 쏘는 move(cs.tp 등)도 keepalive 기준 좌표를
       * 갱신한다 — 안 하면 정지 keepalive 가 옛 좌표(스폰)를 계속 보고해
       * 서버 pose 가 MAX_STEP 클램프로 끌려간다 (2026-09-18, 삽 줍기가
       * 「너무 멉니다」로 흔들린 원인) */
      if (msg.t === 'move') {
        this.lastMoveSent = performance.now();
        this.lastReport = [msg.x, msg.z, msg.yaw, msg.pitch, msg.flags];
        this.lastPos = [msg.x, msg.z];
        this.lastY = msg.y;
      }
      this.ws.send(s);
    }
    else if (msg.t !== 'move' && msg.t !== 'ping' && this.queue.length < 32) this.queue.push(s);
  }

  /**
   * 위치 보고 (REPORT_HZ 로 스로틀).
   * 서버는 이 메시지를 받는 즉시 중계하므로, 가만히 있으면 트래픽이 0 이 된다.
   * 다만 완전히 침묵하면 곤란하니 정지 중에도 1.5초에 한 번은 보낸다.
   */
  reportMove(x: number, y: number, z: number, yaw: number, pitch: number, flags: number, intervalMs: number) {
    const now = performance.now();
    if (now - this.lastMoveSent < intervalMs) return;
    const last = this.lastReport;
    const still =
      last &&
      Math.abs(last[0] - x) < 0.01 &&
      Math.abs(last[1] - z) < 0.01 &&
      Math.abs(last[2] - yaw) < 0.005 &&
      Math.abs(last[3] - pitch) < 0.005 &&
      last[4] === flags;
    if (still && now - this.lastMoveSent < 1500) return;

    this.lastMoveSent = now;
    this.lastReport = [x, z, yaw, pitch, flags];
    this.lastPos = [x, z];
    this.lastY = y;
    this.send({ t: 'move', x: r3(x), y: r3(y), z: r3(z), yaw: r3(yaw), pitch: r3(pitch), flags });
  }

  obj(id: string): ObjState {
    return this.world.objects[id] ?? {};
  }

  /** 내가 들고 있는 pickup prop id 목록 */
  myItems(): string[] {
    return Object.entries(this.world.objects)
      .filter(([, s]) => s.taken && s.by === this.you)
      .map(([id]) => id);
  }

  setPhase(v: Phase) {
    this.send({ t: 'phase', v });
  }

  isHost(): boolean {
    return this.players.get(this.you)?.host === true;
  }
}

function r3(v: number) {
  return Math.round(v * 1000) / 1000;
}
